<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Expectation" tab (FIRST tab).

    WHAT: The metric's EXPECTATION — what healthy results should look like —
      as a DIRECTLY EDITABLE form. This is the document the audit chain judges
      real data against, and the acceptance gate for wizard-built metrics; the
      user owns it. Generated drafts come from the definition agent (Wizard /
      Methodology tabs); every field here can be corrected by hand.

    CONTRACT (props { record, artifact, context }):
      READS  record.composable_config.expectation
      EMITS  patch({ composable_config: { ...config, expectation } }) —
             clone-then-overlay: starts from the EXISTING expectation object and
             overlays the edited fields, so keys this form doesn't know survive
             (the OpportunityConfigPanel rule).

    NOTES:
      - List fields edit as one-item-per-line textareas; patient_row columns as
        "COLUMN_NAME | what to expect" lines.
      - Hand edits change contract_hash, so a wizard approval given before the
        edit is void — the user approves what they read, always.
      - hydrating guard: no spurious patch on mount.
      - NOT selfChrome — rendered inside ArtifactDetail's standard titled card.
  -->
  <div>
    <!-- human seed → model completion → human ownership -->
    <div class="flex items-start gap-3 mb-4 pb-4 border-b border-gray-100">
      <p class="flex-1 text-[11px] text-gray-400 leading-relaxed">
        Write anything, anywhere below — a band, one red flag, a note. Flesh out
        fills every other field around your entries; your words survive verbatim,
        and everything stays editable afterward.
      </p>
      <button
        class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-2 hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        :style="{ background: '#2563EB' }"
        :disabled="fleshing"
        @click="fleshOut"
      >
        <font-awesome-icon :icon="['fas', fleshing ? 'circle-notch' : 'wand-magic-sparkles']" :class="fleshing ? 'fa-spin' : ''" class="text-[10px]" />
        {{ fleshing ? 'Fleshing out…' : 'Flesh out with AI' }}
      </button>
    </div>
    <div v-if="fleshMsg" class="mb-3 text-xs rounded-lg px-3 py-2 border"
         :class="fleshErr ? 'text-red-600 bg-red-50 border-red-200' : 'text-gray-600 bg-gray-50 border-gray-200'">
      {{ fleshMsg }}
    </div>

    <div class="space-y-5">
      <!-- intent + human notes: the human-authored ground truth, first -->
      <div>
        <div class="text-[11px] font-semibold text-sky-700 uppercase tracking-wide mb-1.5">Stated intent</div>
        <textarea v-model="form.intent" rows="2" class="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"
                  placeholder="What this metric is for, in the owner's words. Preserved verbatim across regenerations."></textarea>
        <div class="text-[11px] font-semibold text-sky-700 uppercase tracking-wide mb-1.5 mt-3">Human notes (one per line)</div>
        <textarea v-model="form.human_notes" rows="8" class="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"
                  placeholder="Corrections and practice idiosyncrasies. The audit judge treats these as authoritative."></textarea>
      </div>

      <!-- aggregate -->
      <div class="border-t border-gray-100 pt-4">
        <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Aggregate</div>
        <div class="grid grid-cols-3 gap-2 mb-2">
          <label class="text-xs text-gray-600">Band low
            <input v-model="form.band_low" type="text" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
          </label>
          <label class="text-xs text-gray-600">Band high
            <input v-model="form.band_high" type="text" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
          </label>
          <label class="text-xs text-gray-600">Unit
            <input v-model="form.band_unit" type="text" placeholder="ratio | count | days | dollars" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400" />
          </label>
        </div>
        <div class="grid grid-cols-2 gap-2 mb-2">
          <label class="text-xs text-gray-600">Denominator magnitude
            <textarea v-model="form.denom_magnitude" rows="1" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
          </label>
          <label class="text-xs text-gray-600">Denominator basis
            <textarea v-model="form.denom_basis" rows="1" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
          </label>
        </div>
        <label class="text-xs text-gray-600 block mb-2">Year over year
          <textarea v-model="form.yoy" rows="1" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
        </label>
        <label class="text-xs text-gray-600 block mb-2">Within-year shape (one fact per line)
          <textarea v-model="form.shape" rows="3" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
        </label>
        <label class="text-xs text-gray-600 block">Red flags (one per line)
          <textarea v-model="form.agg_red_flags" rows="3" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
        </label>
      </div>

      <!-- entities -->
      <div class="border-t border-gray-100 pt-4">
        <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Across Entities</div>
        <label class="text-xs text-gray-600 block mb-2">Spread
          <textarea v-model="form.spread" rows="1" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
        </label>
        <label class="text-xs text-gray-600 block mb-2">Legitimate outliers (one archetype per line)
          <textarea v-model="form.legit_outliers" rows="3" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
        </label>
        <label class="text-xs text-gray-600 block">Red flags (one per line)
          <textarea v-model="form.ent_red_flags" rows="2" class="mt-1 w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow"></textarea>
        </label>
      </div>

      <p class="text-[10px] text-gray-400">
        Saved automatically. The audit chain judges real data against exactly this document;
        edits here also void any not-yet-built wizard approval so you re-approve what you wrote.
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const POLL_MS = 5000;
const MAX_POLLS = 120;

export default {
  name: 'ExpectationSection',
  components: { FontAwesomeIcon },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    return {
      form: this.hydrate(),
      hydrating: true,
      fleshing: false,
      fleshMsg: '',
      fleshErr: false,
      pollCount: 0,
    };
  },
  computed: {
    expectation() {
      return (this.record.composable_config || {}).expectation || null;
    },
    hasExpectation() {
      return !!this.expectation;
    },
  },
  watch: {
    // Only accept an emit belonging to the current hydration — a boolean flag
    // cleared on $nextTick loses to the deep watcher's own flush.
    form: {
      deep: true,
      handler() {
        if (this._hydrationToken !== this._liveToken) return;
        this.emit();
      },
    },
    // Rehydrate when the expectation changes underneath us (flesh-out
    // completed and the shell reloaded the record).
    expectation() {
      if (this.fleshing) return;
      this.rehydrate();
    },
  },
  created() {
    // Not in data(): reactive tokens would retrigger the watcher they guard.
    this._liveToken = 0;
    this._hydrationToken = 0;
    // What hydration put in the form. staleFields() compares against this to tell
    // a user's deliberate clear from a field that was never touched.
    this._baseline = { ...this.form };
  },
  mounted() {
    this._hydrationToken = this._liveToken;
    this.hydrating = false;
  },
  beforeUnmount() { clearTimeout(this._pollTimer); },
  methods: {
    async fleshOut() {
      // The form's current state (the human's entries) is already saved via
      // the debounced patch; the worker reads it from the metric. Mode
      // 'expectation' touches ONLY the expectation.
      this.fleshing = true;
      this.fleshErr = false;
      this.fleshMsg = 'The definition agent is completing the expectation around your entries — a few minutes.';
      // Make sure the human's latest keystrokes are PERSISTED before the
      // worker reads the metric: re-emit and outwait the shell's debounce.
      this.emit();
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/generate-contract/`,
          { mode: 'expectation' },
        );
        if (data.status === 'FAILED') throw new Error('The job failed to start.');
        this.pollCount = 0;
        this.schedulePoll(data.job_id);
      } catch (e) {
        this.fleshing = false;
        this.fleshErr = true;
        this.fleshMsg = `Flesh out failed: ${e?.response?.data?.error || e.message}`;
      }
    },
    schedulePoll(jobId) {
      clearTimeout(this._pollTimer);
      this._pollTimer = setTimeout(() => this.poll(jobId), POLL_MS);
    },
    async poll(jobId) {
      this.pollCount += 1;
      try {
        const { data: job } = await axios.get(`/api/personal-analyst/jobs/${jobId}/`);
        if (job.status === 'COMPLETED') {
          this.fleshing = false;
          this.fleshMsg = 'Fleshed out — review and edit below.';
          this.fleshErr = false;
          this.$emit('refresh'); // shell reloads the record → rehydrate watcher
          return;
        }
        if (job.status === 'FAILED') {
          this.fleshing = false;
          this.fleshErr = true;
          this.fleshMsg = `Flesh out failed: ${job.error_message || 'the worker did not complete.'}`;
          return;
        }
      } catch (e) { /* transient poll error — keep trying */ }
      if (this.pollCount >= MAX_POLLS) {
        this.fleshing = false;
        this.fleshErr = true;
        this.fleshMsg = 'Taking too long — check back later.';
        return;
      }
      this.schedulePoll(jobId);
    },
    lines(v) {
      if (Array.isArray(v)) return v.join('\n');
      return v ? String(v) : '';
    },
    unlines(s) {
      return String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);
    },
    hydrate() {
      const e = (this.record.composable_config || {}).expectation || {};
      const agg = e.aggregate || {};
      const band = agg.rate_band || {};
      const denom = agg.denominator || {};
      const ent = e.entity_distribution || {};
      const row = e.patient_row || {};
      return {
        intent: e.intent || '',
        human_notes: this.lines(e.human_notes),
        band_low: band.low ?? '',
        band_high: band.high ?? '',
        band_unit: band.unit || '',
        denom_magnitude: denom.magnitude || '',
        denom_basis: denom.basis || '',
        yoy: agg.yoy || '',
        shape: this.lines(agg.shape),
        agg_red_flags: this.lines(agg.red_flags),
        spread: ent.spread || '',
        legit_outliers: this.lines(ent.legitimate_outliers),
        ent_red_flags: this.lines(ent.red_flags),
      };
    },
    num(v) {
      if (v === '' || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    },
    // Refill from the record and invalidate any emit queued against the old
    // contents. All rehydration goes through here.
    rehydrate() {
      this._liveToken += 1;
      const token = this._liveToken;
      this.hydrating = true;
      this.form = this.hydrate();
      this._baseline = { ...this.form };   // provenance anchor, see staleFields()
      this.$nextTick(() => {
        if (this._liveToken !== token) return;   // superseded mid-flight
        this._hydrationToken = token;
        this.hydrating = false;
      });
    },
    emit() {
      if (this.hydrating) return;
      const f = this.form;
      // Clone-then-overlay at EVERY level: unknown keys on the expectation and
      // its sub-objects survive a save from this form.
      const e = { ...(this.expectation || {}) };
      e.intent = f.intent || null;
      e.human_notes = this.unlines(f.human_notes);
      e.aggregate = {
        ...(e.aggregate || {}),
        rate_band: { ...((e.aggregate || {}).rate_band || {}),
                     low: this.num(f.band_low), high: this.num(f.band_high),
                     unit: f.band_unit || null },
        denominator: { ...((e.aggregate || {}).denominator || {}),
                       magnitude: f.denom_magnitude || null, basis: f.denom_basis || null },
        yoy: f.yoy || null,
        shape: this.unlines(f.shape),
        red_flags: this.unlines(f.agg_red_flags),
      };
      e.entity_distribution = {
        ...(e.entity_distribution || {}),
        spread: f.spread || null,
        legitimate_outliers: this.unlines(f.legit_outliers),
        red_flags: this.unlines(f.ent_red_flags),
      };
      // patient_row / insight_reconciliation are deliberately NOT edited here —
      // the expectation covers the metric itself, not worklist or insight
      // design. Existing keys pass through untouched via the clone above.

      // STALE-FORM GUARD. This section hydrates once; if the definition agent
      // completes server-side afterwards, a later keystroke would write the
      // stale form back over the agent's result (metric 1908 lost a 15,275-char
      // expectation this way). Comparing the outgoing object to the record does
      // NOT catch it — that object is built FROM the record. The signal is a
      // field the record has and the form does not.
      if (this.staleFields().length) {
        this.rehydrate();   // show the agent's result, don't edit a ghost
        return;
      }

      this.$emit('patch', {
        composable_config: { ...(this.record.composable_config || {}), expectation: e },
      });
    },
    // Fields the record has, the form does not, AND the user never touched.
    //
    // PROVENANCE, NOT CONTENT. Emptiness alone cannot tell a stale form from a
    // deliberate delete: both look like "record has text, form does not". Judging
    // on content made clearing a textarea impossible — the guard read it as stale,
    // rehydrated, and the text reappeared. So a field whose value differs from the
    // one hydrated INTO the form was edited by the user here and is never stale;
    // only fields still identical to their hydration baseline can be ghosts.
    staleFields() {
      const prior = this.expectation;
      if (!prior) return [];
      const a = prior.aggregate || {};
      const ent = prior.entity_distribution || {};
      const f = this.form;
      const base = this._baseline || {};
      const filled = (v) => String(v == null ? '' : v).trim().length > 0;
      // Untouched = the form still holds exactly what hydration put there.
      const untouched = (k, formVal) => String(formVal ?? '') === String(base[k] ?? '');
      const out = [];
      for (const [k, stored, formVal] of [
        ['intent', prior.intent, f.intent],
        ['yoy', a.yoy, f.yoy],
        ['denom_basis', (a.denominator || {}).basis, f.denom_basis],
        ['denom_magnitude', (a.denominator || {}).magnitude, f.denom_magnitude],
        ['spread', ent.spread, f.spread],
      ]) {
        if (typeof stored === 'string' && stored.trim() && !filled(formVal)
            && untouched(k, formVal)) out.push(k);
      }
      for (const [k, stored, formVal] of [
        ['human_notes', prior.human_notes, f.human_notes],
        ['shape', a.shape, f.shape],
        ['agg_red_flags', a.red_flags, f.agg_red_flags],
        ['legit_outliers', ent.legitimate_outliers, f.legit_outliers],
        ['ent_red_flags', ent.red_flags, f.ent_red_flags],
      ]) {
        if (Array.isArray(stored) && stored.length && !filled(formVal)
            && untouched(k, formVal)) out.push(k);
      }
      return out;
    },
  },
};
</script>

<style scoped>
/* Auto-size to content — the solved problem. Modern Chrome grows the
   box as you type; older engines keep min-height + a manual resize handle. */
textarea.autogrow {
  field-sizing: content;
  min-height: 2.6rem;
  max-height: 60vh;
  resize: vertical;
  overflow-y: auto;
}
</style>
